import { Fragment, useState, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Accordion from '@JohnLewisPartnership/jl-design-system/components/accordion/Accordion';
import { UnderlinedButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';

import facetType from '../../../utils/types/facet';
import FacetItem from '../facet-item/FacetItem';
import FacetSearch from '../facet-search/FacetSearch';
import getFilteredValues from './selectors/filterValues';
import { getSelectedValues } from './selectors/selectedValues';
import styles from './FacetAccordion.module.scss';
import { useFacetsData } from '../data/useFacetsData';

const SEARCH_LENGTH = 21;
const INITIAL_NUMBER_TO_SHOW = 8;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 10;

const FacetAccordion = ({
  facet = {},
  href = '',
  initialSearch = '',
  isFilterDrawer = false,
  isShown = true,
  opened = false,
  position = 1,
  showColourFilterSwatches = false,
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);
  const {
    experiences: { isSingleColumnLayout },
  } = useFacetsData();

  const { label, dimensionName } = facet;

  const isFacetListSearchable = useMemo(
    () => facet.values.length >= SEARCH_LENGTH,
    [facet.values.length]
  );

  const { numberOfOwnBrandItems, items } = useMemo(
    () => getFilteredValues(facet.values, search),
    [facet.values, search]
  );

  const itemsToDisplay = items;

  const selectedItems = getSelectedValues(facet.values);

  const numberOfValuesToShow =
    itemsToDisplay.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING &&
    !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : itemsToDisplay.length;

  const handleSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleClear = useCallback(() => {
    setSearch('');
  }, []);

  const handleAccordionToggle = useCallback(() => {}, []);

  const toggleShowMore = useCallback(
    (e) => {
      // Stop this event propagating to the parent Accordion so that the click does not get handled there
      // (which would lead to the search value being cleared)
      e.stopPropagation();
      setIsFacetListExpanded(!isFacetListExpanded);
    },
    [isFacetListExpanded]
  );

  const numberOfHiddenValues =
    itemsToDisplay.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING
      ? itemsToDisplay.length - INITIAL_NUMBER_TO_SHOW
      : 0;

  const facetLabel = `${
    isFilterDrawer ? 'drawer-' : ''
  }accordion-summary-${label.replace(/ /g, '-').toLowerCase()}`;

  const isShowMoreOrFewerVisible =
    itemsToDisplay.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const showMoreOrFewerButton = useCallback(
    () => (
      <UnderlinedButton
        className={styles.showMoreFacetValues}
        onClick={toggleShowMore}
        data-testid={`show-${isFacetListExpanded ? 'fewer' : 'more'}-button`}
      >
        {isFacetListExpanded
          ? 'Show fewer'
          : `Show ${numberOfHiddenValues} more`}
      </UnderlinedButton>
    ),
    [isFacetListExpanded, numberOfHiddenValues]
  );

  const showCollapsedFilterSummary = selectedItems.length > 0;

  const isColourSwatch =
    dimensionName === 'skus.basicColor' ||
    dimensionName === 'childSKUs.basicColor';

  return (
    <Fragment>
      <Accordion
        animated={false}
        onClick={handleAccordionToggle}
        data-testid={`accordion-${label.toLowerCase()}`}
        data-label={label}
        data-test2={`position${position}`}
        key={label}
        label={
          <Fragment>
            <h3 className={styles.facetLabel} id={facetLabel}>
              {label}
            </h3>
            {showCollapsedFilterSummary && (
              <p className={styles.collapsedSummary}>
                {selectedItems.map(
                  (value, index) =>
                    `${index > 0 ? ', ' : ''}${value.label} (${value.qty})`
                )}
              </p>
            )}
          </Fragment>
        }
        open={opened}
        size="S"
        className={classnames(styles.accordion, {
          [styles['accordion--is-hidden']]: !isShown,
        })}
      >
        <div aria-labelledby={facetLabel} role="group">
          {isFacetListSearchable && (
            <FacetSearch
              label={label}
              search={search}
              onSearch={handleSearch}
              onClear={handleClear}
              isFilterDrawer={isFilterDrawer}
            />
          )}
          <ul
            className={classnames(styles.list, {
              [styles['list--two-columns']]:
                isColourSwatch && isFilterDrawer && isSingleColumnLayout,
            })}
          >
            {itemsToDisplay.length === 0 && (
              <li>
                <p
                  className={styles.noResults}
                  data-testid="facet-search-no-results"
                >
                  No results were found for {`'${search}'`}. Please try another
                  search.
                </p>
              </li>
            )}
            {itemsToDisplay.map((value, index) => (
              <Fragment key={value.name}>
                <FacetItem
                  href={href}
                  key={value.name}
                  value={value}
                  dimensionName={dimensionName}
                  showColourFilterSwatches={showColourFilterSwatches}
                  onFacetClicked={handleClear}
                  hidden={index >= numberOfValuesToShow ? true : undefined}
                  isFilterDrawer={isFilterDrawer}
                  last={
                    index + 1 === numberOfValuesToShow ||
                    (index === INITIAL_NUMBER_TO_SHOW - 1 &&
                      isFacetListSearchable &&
                      isShowMoreOrFewerVisible &&
                      isFacetListExpanded)
                  }
                  divider={index === numberOfOwnBrandItems - 1}
                />
                {index === INITIAL_NUMBER_TO_SHOW - 1 &&
                  isFacetListSearchable &&
                  isShowMoreOrFewerVisible &&
                  isFacetListExpanded &&
                  showMoreOrFewerButton()}
              </Fragment>
            ))}
          </ul>
          {isShowMoreOrFewerVisible && showMoreOrFewerButton()}
        </div>
      </Accordion>
    </Fragment>
  );
};

FacetAccordion.propTypes = {
  facet: facetType,
  href: PropTypes.string,
  initialSearch: PropTypes.string,
  isFilterDrawer: PropTypes.bool,
  isShown: PropTypes.bool,
  opened: PropTypes.bool,
  position: PropTypes.number,
  showColourFilterSwatches: PropTypes.bool,
};

export default FacetAccordion;
